import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Wand2, Check, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const SOURCE_CHIP = {
  "original year": "bg-emerald-500/15 text-emerald-300",
  "MusicBrainz first release": "bg-cyan-500/15 text-cyan-300",
  "tag year": "bg-bg-elevated text-ink-secondary",
  "compilation album year": "bg-amber-500/15 text-amber-300",
};

export const DecadeAutoModal = ({ onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post("/music/decades/auto", { mode: "preview" })
      .then((r) => {
        setData(r.data);
        setSel(new Set((r.data.proposals || []).filter((p) => !p.flag && p.proposed !== "Unknown").map((p) => p.track_id)));
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Preview failed"); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/music/decades/auto", { mode: "apply", track_ids: [...sel] });
      toast.success(`${r.data.updated} track${r.data.updated === 1 ? "" : "s"} organized into decades — undo anytime per track`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3" onClick={() => !busy && onClose()}
      data-testid="ml-decade-auto-modal">
      <div className="w-full max-w-2xl rounded-xl border border-line bg-bg-surface p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="type-section-heading text-ink-primary flex items-center gap-2">
            <Wand2 size={15} className="text-brand-amber" /> Auto-assign decades
          </h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="ml-decade-auto-close"><X size={15} /></button>
        </div>
        <p className="type-body text-ink-muted mb-3">
          Decades use the <b>original recording year</b> — manual original years first, then MusicBrainz first-release
          dates for identified recordings, then the file's tag year. Years coming from a <b>compilation album</b> are
          flagged and left unchecked: review them before applying. Nothing changes until you approve.
        </p>

        {!data ? <Loader2 size={16} className="animate-spin text-ink-muted" /> : (
          <>
            {data.proposals.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line p-6 text-center type-body text-ink-muted" data-testid="ml-decade-auto-empty">
                <Check size={18} className="mx-auto mb-2 text-emerald-400" />
                Every track already has a decade (or no usable year was found).
              </div>
            ) : (
              <div className="rounded-lg border border-line divide-y divide-line max-h-[48vh] overflow-y-auto" data-testid="ml-decade-auto-list">
                {data.proposals.map((p) => (
                  <label key={p.track_id} className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-2 cursor-pointer hover:bg-white/5"
                    data-testid={`ml-decade-auto-row-${p.track_id}`}>
                    <input type="checkbox" checked={sel.has(p.track_id)} onChange={() => toggle(p.track_id)} className="accent-amber-500" />
                    <div className="min-w-[150px] flex-1">
                      <div className="type-list-title text-ink-primary truncate">{p.title}</div>
                      <div className="type-list-subtitle text-ink-muted truncate">{p.artist || "Unknown"} · {p.album || "—"}</div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-mono ${SOURCE_CHIP[p.source] || "bg-bg-elevated text-ink-muted"}`}>
                      {p.flag && <AlertTriangle size={9} className="inline mr-0.5 -mt-0.5" />}
                      {p.year_used || "no year"}{p.source ? ` · ${p.source}` : ""}
                    </span>
                    <span className="shrink-0 text-[11px] font-mono text-ink-muted">{p.current} →</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${p.proposed === "Unknown" ? "bg-slate-500/20 text-slate-300" : "bg-brand-amber/15 text-brand-amber"}`}>
                      {p.proposed}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
              <span className="type-timestamp text-ink-muted">
                {data.proposals.length} proposal{data.proposals.length === 1 ? "" : "s"} · {sel.size} approved
                {data.mb_lookups_used ? ` · ${data.mb_lookups_used} MusicBrainz lookups` : ""}
              </span>
              <button onClick={apply} disabled={!sel.size || busy} data-testid="ml-decade-auto-apply"
                className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-bg-base disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                Apply {sel.size} change{sel.size === 1 ? "" : "s"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
